import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Button, Spinner } from '../design-system';
import { roleConsequences, rolesLabel } from '../../lib/adminUsers';
import type { UserRole } from '../../types';

const ALL_ROLES: UserRole[] = ['traveler', 'host', 'admin'];

/**
 * Change one person's roles. Says what the change means before it happens, asks
 * for a typed word before anyone is made an admin, and shows the server's
 * reason when it refuses (your own admin role, a deleted account).
 */
export function RolesDialog({ userId, name, roles, isSelf, onClose, onSaved }: {
  userId: string;
  name: string;
  roles: UserRole[];
  isSelf: boolean;
  onClose: () => void;
  onSaved: (roles: UserRole[]) => void;
}) {
  const [next, setNext] = useState<UserRole[]>(roles);
  const [typed, setTyped] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const changed = rolesLabel(next) !== rolesLabel(roles);
  const grantsAdmin = next.includes('admin') && !roles.includes('admin');
  const consequences = roleConsequences(roles, next);
  const ready = changed && next.length > 0 && (!grantsAdmin || typed.trim().toUpperCase() === 'ADMIN');

  function toggle(r: UserRole) {
    setNext((cur) => (cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r]));
  }

  async function save() {
    setSaving(true);
    setError(null);
    const { data, error: err } = await supabase.rpc('admin_set_user_roles', { p_user_id: userId, p_roles: next });
    setSaving(false);
    if (err) {
      setError(err.message || "We couldn't change the roles.");
      return;
    }
    onSaved(((data as UserRole[] | null) ?? next));
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 px-4" onClick={saving ? undefined : onClose}>
      <div role="dialog" aria-label="Change roles" className="bg-app-card border border-app-border rounded-2xl shadow-xl w-full max-w-md p-5 flex flex-col gap-4" onClick={(e) => e.stopPropagation()}>
        <div>
          <h2 className="text-lg font-semibold text-app-fg">Change roles</h2>
          <p className="text-sm text-app-muted">{name}</p>
        </div>

        <div className="flex flex-col gap-2">
          {ALL_ROLES.map((r) => {
            const lockedOwnAdmin = isSelf && r === 'admin' && roles.includes('admin');
            return (
              <label key={r} className={`flex items-center gap-2 text-sm text-app-fg ${lockedOwnAdmin ? 'opacity-60' : 'cursor-pointer'}`}>
                <input type="checkbox" checked={next.includes(r)} disabled={saving || lockedOwnAdmin} onChange={() => toggle(r)} />
                <span className="capitalize">{r}</span>
                {lockedOwnAdmin && <span className="text-xs text-app-muted">(you can't remove your own admin role)</span>}
              </label>
            );
          })}
        </div>

        {consequences.length > 0 && (
          <ul className="text-sm text-app-fg list-disc pl-5 flex flex-col gap-1">
            {consequences.map((c) => <li key={c}>{c}</li>)}
          </ul>
        )}

        {grantsAdmin && (
          <div className="flex flex-col gap-1">
            <label htmlFor="confirm-admin" className="text-sm text-app-muted">Type ADMIN to confirm</label>
            <input
              id="confirm-admin"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              className="w-full bg-app-bg border border-app-border rounded-lg px-3 py-2 text-sm text-app-fg"
            />
          </div>
        )}

        {next.length === 0 && <p className="text-sm text-app-muted">Pick at least one role.</p>}
        {error && <p role="alert" className="text-sm text-crimson">{error}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={() => void save()} disabled={!ready || saving}>
            {saving ? <Spinner size="sm" className="text-ink" /> : 'Save roles'}
          </Button>
        </div>
      </div>
    </div>
  );
}
